/**
 * The note a trainer sends with a nudge.
 *
 * Opens prefilled with what the server would say on its own, so sending is one
 * click — but the words are the trainer's if they want them to be. The reminder
 * lands in the learner's notifications and as a direct message they can answer
 * (Aleix, 2026-09-21).
 */
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import { errorMessageFrom } from '../../api/client';
import { useRemindLearnerAboutCourse } from '../../services/learners';
import type { LearnerCourseRow } from './LearnerCourseRoster';

interface Props {
  learnerId: string;
  learnerName: string;
  row: LearnerCourseRow;
  onClose: () => void;
}

const MAX = 500;

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })
    : null;

/** The sentence the server writes when no note is given. */
export function defaultReminder(row: LearnerCourseRow): string {
  if (!row.due_date) return `A reminder to finish "${row.title}".`;
  const overdue = new Date(row.due_date).getTime() < Date.now();
  return (
    `A reminder to finish "${row.title}" — it ` +
    `${overdue ? 'was' : 'is'} due on ${formatDate(row.due_date)}.`
  );
}

const RemindLearnerDialog: React.FC<Props> = ({
  learnerId,
  learnerName,
  row,
  onClose,
}) => {
  const [message, setMessage] = useState(() => defaultReminder(row));
  const remind = useRemindLearnerAboutCourse(learnerId);

  const send = async () => {
    try {
      await remind.mutateAsync({
        assignmentId: row.assignment_id,
        message: message.trim() || undefined,
      });
      toast.success(`Reminder sent to ${learnerName}.`);
      onClose();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 429) {
        // The cooldown is a kindness to the learner, so say so plainly.
        toast.error(
          `${learnerName} was already reminded about this course recently.`
        );
        onClose();
        return;
      }
      toast.error(errorMessageFrom(err) ?? 'Could not send the reminder.');
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Remind ${learnerName}`}
      data-testid="remind-learner-dialog"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <p className="min-w-0 flex-1 text-xs text-text-secondary">
            Goes to their notifications and your chat with them.
          </p>
          <div className="flex shrink-0 gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full px-4 py-2 text-sm font-bold text-text-secondary hover:text-text-primary focus-ring transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={send}
              disabled={remind.isPending}
              data-testid="send-reminder-btn"
              className="min-w-[124px] rounded-full bg-primary-500 px-5 py-2 text-sm font-bold hover:bg-primary-600 dark:hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed focus-ring transition-colors"
            >
              {remind.isPending ? 'Sending…' : 'Send reminder'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <label
          htmlFor="reminder-message"
          className="block text-xs font-bold uppercase tracking-wide text-text-tertiary"
        >
          Message
        </label>
        <textarea
          id="reminder-message"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
          rows={4}
          className="input w-full resize-y"
          placeholder={defaultReminder(row)}
        />
        <p className="text-right text-xs text-text-tertiary">
          {message.length}/{MAX}
        </p>
      </div>
    </Modal>
  );
};

export default RemindLearnerDialog;
